// ===========================================
// Attendance React Query Hooks
// ===========================================
// Custom hooks for attendance API operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiErrorMessage } from '../utils/apiError';
import {
  attendanceApi,
  BatchMarkAttendanceInput,
  AttendanceByLessonFilter,
  AttendanceByStudentFilter,
  AttendanceReportFilter,
  TodayAttendanceFilter,
} from '../api/attendance.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const attendanceKeys = {
  all: ['attendance'] as const,
  today: (filters?: TodayAttendanceFilter) =>
    [...attendanceKeys.all, 'today', filters] as const,
  detail: (id: string) =>
    [...attendanceKeys.all, 'detail', id] as const,
  byLesson: (lessonId: string, filters?: AttendanceByLessonFilter) =>
    [...attendanceKeys.all, 'lesson', lessonId, filters] as const,
  enrolledStudents: (lessonId: string, date: string, studentId?: string) =>
    [...attendanceKeys.all, 'students', lessonId, date, studentId ?? null] as const,
  rosterStatus: (keys: string[]) =>
    [...attendanceKeys.all, 'roster-status', keys] as const,
  report: (lessonId: string, filters?: AttendanceReportFilter) =>
    [...attendanceKeys.all, 'report', lessonId, filters] as const,
  byStudent: (studentId: string, filters?: AttendanceByStudentFilter) =>
    [...attendanceKeys.all, 'student', studentId, filters] as const,
  studentStats: (studentId: string) =>
    [...attendanceKeys.all, 'stats', studentId] as const,
};

// ===========================================
// QUERIES
// ===========================================

/**
 * Get today's attendance for the school
 */
export function useTodayAttendance(filters?: TodayAttendanceFilter) {
  return useQuery({
    queryKey: attendanceKeys.today(filters),
    queryFn: () => attendanceApi.getToday(filters),
  });
}

/**
 * Get a single attendance record
 */
export function useAttendance(id: string) {
  return useQuery({
    queryKey: attendanceKeys.detail(id),
    queryFn: () => attendanceApi.getById(id),
    enabled: !!id,
  });
}

/**
 * Get attendance records for a lesson
 */
export function useAttendanceByLesson(lessonId: string, filters?: AttendanceByLessonFilter) {
  return useQuery({
    queryKey: attendanceKeys.byLesson(lessonId, filters),
    queryFn: () => attendanceApi.getByLesson(lessonId, filters),
    enabled: !!lessonId,
  });
}

/**
 * Get enrolled students with attendance for a lesson on a specific date.
 * When `studentId` is supplied, the response is narrowed to a single
 * student — used by the Teacher Tasks page for hybrid individual sessions.
 */
export function useEnrolledStudentsForAttendance(
  lessonId: string,
  date: string,
  studentId?: string
) {
  return useQuery({
    queryKey: attendanceKeys.enrolledStudents(lessonId, date, studentId),
    queryFn: () => attendanceApi.getEnrolledStudents(lessonId, date, studentId),
    enabled: !!lessonId && !!date,
  });
}

/**
 * Roster + attendance + reported absence for a set of occurrences in ONE call
 * (teacher dashboard, 2026-09-07). Keyed under `attendanceKeys.all`, so marking
 * attendance — and an absence report/cancel — refreshes it.
 */
export function useRosterStatus(occurrences: Array<{ lessonId: string; dateKey: string }>) {
  const keys = [...new Set(occurrences.map((o) => `${o.lessonId}:${o.dateKey}`))].sort();
  return useQuery({
    queryKey: attendanceKeys.rosterStatus(keys),
    queryFn: () =>
      attendanceApi.getRosterStatus(
        keys.map((k) => {
          const [lessonId, dateKey] = k.split(':');
          return { lessonId, dateKey };
        })
      ),
    enabled: keys.length > 0,
  });
}

/**
 * Get attendance report for a lesson
 */
export function useAttendanceReport(lessonId: string, filters?: AttendanceReportFilter) {
  return useQuery({
    queryKey: attendanceKeys.report(lessonId, filters),
    queryFn: () => attendanceApi.getReport(lessonId, filters),
    enabled: !!lessonId,
  });
}

/**
 * Get attendance history for a student
 */
export function useAttendanceByStudent(studentId: string, filters?: AttendanceByStudentFilter) {
  return useQuery({
    queryKey: attendanceKeys.byStudent(studentId, filters),
    queryFn: () => attendanceApi.getByStudent(studentId, filters),
    enabled: !!studentId,
  });
}

/**
 * Get attendance statistics for a student
 */
export function useStudentAttendanceStats(studentId: string) {
  return useQuery({
    queryKey: attendanceKeys.studentStats(studentId),
    queryFn: () => attendanceApi.getStudentStats(studentId),
    enabled: !!studentId,
  });
}

// ===========================================
// MUTATIONS
// ===========================================

/**
 * Mark attendance for multiple students in a lesson
 */
export function useBatchMarkAttendance() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: BatchMarkAttendanceInput) => attendanceApi.batchMark(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.byLesson(variables.lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.enrolledStudents(variables.lessonId, variables.date),
      });
      enqueueSnackbar('Attendance saved successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(apiErrorMessage(error, 'Failed to save attendance'), { variant: 'error' });
    },
  });
}
